import { useEffect, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';

const PREANAMNESE_SCHEMA = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Pré-Anamnese Digital | Como Funciona | Neuro Elos',
    description:
      'Saiba o que é a pré-anamnese neuropsicopedagógica, como funciona, quanto tempo leva e por que é essencial antes da primeira sessão de avaliação.',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: 'https://neuroelos.com.br/' },
        { '@type': 'ListItem', position: 2, name: 'Pré-Anamnese', item: 'https://neuroelos.com.br/pre-anamnese' },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'Como funciona a Pré-Anamnese neuropsicopedagógica',
    description:
      'Processo passo a passo da pré-anamnese digital antes da primeira sessão de avaliação neuropsicopedagógica na Neuro Elos.',
    totalTime: 'PT20M',
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Agendamento Inicial',
        text: 'O processo começa com um contato via WhatsApp. A família agenda uma conversa rápida para entender a necessidade e confirmar a disponibilidade de datas.',
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Envio da Pré-Anamnese',
        text: 'A família recebe um formulário digital detalhado para preencher com calma em casa. O formulário cobre histórico de desenvolvimento, escolar, médico e familiar da criança.',
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'Análise Clínica Prévia',
        text: 'Com as informações em mãos, a profissional realiza uma leitura clínica aprofundada antes da primeira sessão, permitindo um olhar já orientado e personalizado para cada criança.',
      },
      {
        '@type': 'HowToStep',
        position: 4,
        name: 'Primeira Sessão de Acolhimento',
        text: 'Com o histórico já analisado, a primeira sessão é 100% dedicada ao acolhimento da criança e da família — sem burocracia. Apenas presença e escuta.',
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'O que é a pré-anamnese neuropsicopedagógica?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A pré-anamnese neuropsicopedagógica é um questionário clínico estruturado enviado digitalmente aos responsáveis antes da primeira sessão de avaliação. Coleta informações sobre desenvolvimento, saúde, escola e histórico familiar da criança para orientar o processo avaliativo com precisão.',
        },
      },
      {
        '@type': 'Question',
        name: 'Quanto tempo leva para preencher a pré-anamnese?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O formulário de Pré-Anamnese leva em média 15 a 20 minutos para ser preenchido. Pode ser feito com calma, no seu ritmo, sem pressão, de qualquer dispositivo com acesso à internet.',
        },
      },
      {
        '@type': 'Question',
        name: 'A pré-anamnese é segura e confidencial?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sim. Todas as informações são protegidas por criptografia e acessadas exclusivamente pela profissional responsável, em conformidade com a LGPD (Lei nº 13.709/2018). Nenhuma informação é compartilhada com terceiros.',
        },
      },
      {
        '@type': 'Question',
        name: 'Quem deve preencher a pré-anamnese?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Os responsáveis legais pela criança — preferencialmente quem acompanha o dia a dia dela. Quanto mais detalhado o preenchimento, mais preciso e acolhedor será o processo avaliativo.',
        },
      },
      {
        '@type': 'Question',
        name: 'Por que a pré-anamnese é importante antes da avaliação?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A Pré-Anamnese permite que a profissional chegue à primeira sessão com um olhar já orientado e personalizado. Isso torna o processo mais preciso, evita retrabalho e garante que nenhuma informação relevante seja esquecida durante a sessão.',
        },
      },
    ],
  },
];

function NeuralBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const NODE_COUNT = 55;
    const MAX_DIST = 180;

    interface Node {
      x: number; y: number;
      vx: number; vy: number;
      r: number;
      pulse: number;
      pulseSpeed: number;
    }

    const nodes: Node[] = Array.from({ length: NODE_COUNT }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      r: Math.random() * 2.5 + 1.5,
      pulse: Math.random() * Math.PI * 2,
      pulseSpeed: Math.random() * 0.02 + 0.01,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        n.pulse += n.pulseSpeed;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
      });

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < MAX_DIST) {
            const alpha = (1 - dist / MAX_DIST) * 0.25;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(230, 210, 200, ${alpha})`;
            ctx.lineWidth = 0.8;
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      nodes.forEach((n) => {
        const pulseR = n.r + Math.sin(n.pulse) * 1.2;
        ctx.beginPath();
        ctx.arc(n.x, n.y, pulseR, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(240, 200, 180, 0.7)`;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(n.x, n.y, pulseR * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(240, 200, 180, 0.08)`;
        ctx.fill();
      });

      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ display: 'block' }}
    />
  );
}

const steps = [
  {
    number: '01',
    icon: 'ri-calendar-check-line',
    title: 'Agendamento Inicial',
    desc: 'O processo começa com um contato via WhatsApp. A família agenda uma conversa rápida para entender a necessidade e confirmar a disponibilidade de datas.',
    color: 'bg-teal/10 text-teal border-teal/20',
    accent: 'text-teal',
  },
  {
    number: '02',
    icon: 'ri-file-text-line',
    title: 'Envio da Pré-Anamnese',
    desc: 'A família recebe um formulário digital detalhado para preencher com calma em casa — sem pressa. O formulário cobre histórico de desenvolvimento, escolar, médico e familiar da criança.',
    color: 'bg-coral/10 text-coral border-coral/20',
    accent: 'text-coral',
  },
  {
    number: '03',
    icon: 'ri-search-eye-line',
    title: 'Análise Clínica Prévia',
    desc: 'Com as informações em mãos, a profissional realiza uma leitura clínica aprofundada antes da primeira sessão. Isso permite um olhar já orientado e personalizado para cada criança.',
    color: 'bg-teal/10 text-teal border-teal/20',
    accent: 'text-teal',
  },
  {
    number: '04',
    icon: 'ri-heart-pulse-line',
    title: 'Primeira Sessão de Acolhimento',
    desc: 'Com o histórico já analisado, a primeira sessão é 100% dedicada ao acolhimento da criança e da família — sem burocracia, sem papelada. Apenas presença e escuta.',
    color: 'bg-coral/10 text-coral border-coral/20',
    accent: 'text-coral',
  },
];

const infoCards = [
  {
    icon: 'ri-time-line',
    title: 'Quanto tempo leva?',
    desc: 'O formulário de Pré-Anamnese leva em média 15 a 20 minutos para ser preenchido. Pode ser feito com calma, no seu ritmo, sem pressão.',
  },
  {
    icon: 'ri-question-answer-line',
    title: 'O que é perguntado?',
    desc: 'São abordados dados da gestação, desenvolvimento motor e de linguagem, histórico escolar, médico, uso de medicamentos, histórico familiar e expectativas dos responsáveis.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'É seguro?',
    desc: 'Sim. Todas as informações são protegidas por criptografia e acessadas exclusivamente pela profissional responsável, em conformidade com a LGPD (Lei nº 13.709/2018).',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'Quem deve preencher?',
    desc: 'Os responsáveis legais pela criança — preferencialmente quem acompanha o dia a dia dela. Quanto mais detalhado, melhor o aproveitamento do processo avaliativo.',
  },
  {
    icon: 'ri-lightbulb-line',
    title: 'Por que é importante?',
    desc: 'A Pré-Anamnese permite uma avaliação mais precisa desde o início, evitando retrabalho e garantindo que nenhuma informação relevante seja esquecida durante a sessão.',
  },
  {
    icon: 'ri-lock-password-line',
    title: 'Acesso exclusivo',
    desc: 'Apenas você e a profissional têm acesso aos dados fornecidos. Nenhuma informação é compartilhada com terceiros ou utilizada fora do contexto clínico.',
  },
];

const highlights = [
  { value: '15 min', label: 'tempo médio de preenchimento' },
  { value: '100%', label: 'da 1ª sessão focada no acolhimento' },
  { value: 'LGPD', label: 'proteção total dos seus dados' },
  { value: 'Digital', label: 'preencha de onde estiver' },
];

export default function PreAnamnese() {
  return (
    <div className="min-h-screen bg-cream font-sans">
      <SEOHead
        title="Pré-Anamnese Digital | Antes da 1ª Sessão | Neuro Elos São Paulo"
        description="Saiba o que é a Pré-Anamnese neuropsicopedagógica, como funciona, quanto tempo leva e por que é essencial. Formulário digital seguro, protegido pela LGPD. Parte da avaliação infantil da Neuro Elos em São Paulo."
        keywords="pré-anamnese neuropsicopedagógica, formulário avaliação infantil, anamnese psicopedagógica, histórico desenvolvimento infantil, LGPD saúde infantil"
        ogTitle="Pré-Anamnese Digital — Neuro Elos"
        ogDescription="O formulário de pré-anamnese leva 15 minutos e transforma toda a jornada avaliativa. Descubra como funciona."
        lastModified="2026-03-28"
        schema={PREANAMNESE_SCHEMA}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 text-white overflow-hidden">
        {/* Neural network animated background */}
        <NeuralBackground />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-teal-dark/90 to-navy/95" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 bg-white/10 text-white/80 text-xs font-700 font-sans uppercase tracking-widest px-4 py-1.5 rounded-full mb-5 md:mb-6">
              <i className="ri-information-line text-coral-light" />
              Entenda o processo
            </span>
            <h1 className="font-serif text-3xl md:text-5xl font-600 mb-4 md:mb-5 leading-tight">
              Como funciona a<br />
              <span className="text-coral-light">Pré-Anamnese?</span>
            </h1>
            <p className="text-white/75 font-sans text-base md:text-lg font-300 leading-relaxed">
              Antes da primeira sessão, coletamos o histórico completo da criança de forma
              digital, segura e no conforto de casa. Isso transforma toda a jornada avaliativa.
            </p>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-10 md:py-12 bg-white border-b border-beige-dark">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
            {highlights.map((h) => (
              <div key={h.value} className="text-center">
                <p className="font-serif text-3xl md:text-4xl font-700 text-teal mb-1">{h.value}</p>
                <p className="text-xs font-sans text-slate-500 uppercase tracking-wider leading-snug">{h.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* O que é */}
      <section className="py-14 md:py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <span className="text-xs font-700 font-sans text-coral uppercase tracking-widest mb-4 block">O que é</span>
              <h2 className="font-serif text-2xl md:text-3xl font-600 text-navy mb-5 md:mb-6 leading-snug">
                A base clínica que antecede<br />toda avaliação
              </h2>
              <p className="text-slate-600 font-sans text-base leading-relaxed mb-4 md:mb-5">
                A <strong className="text-navy">Pré-Anamnese</strong> é um questionário clínico estruturado, enviado digitalmente
                aos responsáveis antes da primeira sessão de avaliação neuropsicopedagógica.
              </p>
              <p className="text-slate-600 font-sans text-base leading-relaxed mb-4 md:mb-5">
                Por meio dele, coletamos informações essenciais sobre o histórico da criança —
                desenvolvimento, saúde, escola, família — de forma organizada e confidencial.
              </p>
              <p className="text-slate-600 font-sans text-base leading-relaxed">
                Essa etapa permite que a profissional chegue à primeira sessão com um olhar
                já orientado, tornando o processo muito mais preciso e acolhedor.
              </p>
            </div>
            <div className="relative mt-6 lg:mt-0">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20minimal%20illustration%20of%20a%20mother%20filling%20out%20a%20digital%20form%20on%20tablet%20at%20home%20with%20soft%20natural%20light%20calm%20cozy%20atmosphere%20neutral%20tones%20beige%20cream%20off%20white%20clean%20background%20professional%20healthcare%20aesthetic&width=600&height=500&seq=preanamnese-info-01&orientation=landscape"
                alt="Mãe preenchendo formulário digital em casa"
                className="w-full h-56 md:h-80 object-cover object-top rounded-2xl"
                loading="lazy"
              />
              <div className="absolute -bottom-4 md:-bottom-5 -left-4 md:-left-5 bg-teal text-white px-4 md:px-5 py-2.5 md:py-3 rounded-xl">
                <p className="text-xs font-700 font-sans uppercase tracking-wider">Confidencial</p>
                <p className="text-xs md:text-sm font-sans font-300">Protegido pela LGPD</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Passo a passo */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-14">
            <span className="text-xs font-700 font-sans text-coral uppercase tracking-widest mb-3 block">Etapas</span>
            <h2 className="font-serif text-2xl md:text-3xl font-600 text-navy">O passo a passo do processo</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {steps.map((s) => (
              <div key={s.number} className={`relative p-5 md:p-6 rounded-2xl border ${s.color} flex flex-col gap-3 md:gap-4`}>
                <span className={`font-serif text-4xl font-700 ${s.accent} opacity-20 leading-none`}>{s.number}</span>
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl bg-white border ${s.color}`}>
                  <i className={`${s.icon} text-lg ${s.accent}`} />
                </div>
                <h3 className="font-serif text-base font-600 text-navy leading-snug">{s.title}</h3>
                <p className="text-sm font-sans text-slate-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Informações detalhadas */}
      <section className="py-14 md:py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-14">
            <span className="text-xs font-700 font-sans text-coral uppercase tracking-widest mb-3 block">Tire suas dúvidas</span>
            <h2 className="font-serif text-2xl md:text-3xl font-600 text-navy">Tudo que você precisa saber</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {infoCards.map((card) => (
              <div key={card.title} className="bg-white rounded-2xl p-5 md:p-6 border border-beige-dark flex flex-col gap-3 md:gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-teal/10 rounded-xl">
                  <i className={`${card.icon} text-teal text-xl`} />
                </div>
                <h3 className="font-serif text-base font-600 text-navy">{card.title}</h3>
                <p className="text-sm font-sans text-slate-500 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-teal-dark to-navy">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-serif text-2xl md:text-3xl font-600 text-white mb-4">
            Pronto para dar o primeiro passo?
          </h2>
          <p className="text-white/70 font-sans text-base leading-relaxed mb-8 md:mb-10">
            Entre em contato pelo WhatsApp e inicie o processo. Após o agendamento,
            você receberá o formulário de Pré-Anamnese diretamente no seu e-mail.
          </p>
          <a
            href="https://wa.me/5511981882825"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-coral text-white px-7 md:px-8 py-3.5 md:py-4 rounded-full text-base font-600 font-sans whitespace-nowrap cursor-pointer transition-all duration-200 hover:bg-coral-dark"
          >
            <i className="ri-whatsapp-line text-lg" />
            Agendar via WhatsApp
          </a>
          <p className="text-white/40 text-xs font-sans mt-5 md:mt-6">
            Atendimento presencial e online · Agendamento rápido e sem burocracia
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
